import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from './auth/AuthContext';
import { useConfiguratorState } from './hooks/useConfiguratorState';
import { loadGoogleFont } from './utils/fontLoader';
import { api } from './api/api';
import { useViewportSize } from './hooks/useViewport';

import {
  Sidebar,
  Section,
  UndoIcon,
  RedoIcon,
  ResponsiveDeviceToggle,
} from './components/common';
import { ProfileModal } from './components/common/ProfileModal';
import { EyeIcon } from './components/common';
import { ClearIcon } from './components/common';
import { deriveDefaultsFromGlobals } from './utils/defaults';
import { DEVICE_PRESETS, ensureDeviceKey } from './utils/devicePresets';
import { applyTextTemplateToSettings } from './utils/textTemplateSync';

import { AdminPanel } from './components/admin/AdminPanel';
import TemplateSelectSection from './components/configurator/TemplateSelectSection';

import AppearanceSection from './components/configurator/AppearanceSection';
import ContentSection from './components/configurator/ContentSection';
import CalendarButtonSection from './components/configurator/CalendarButtonSection';
import WalletSection from './components/configurator/WalletSection';
import WalletImageResizerSection from './components/configurator/WalletImageResizerSection';
import ImagesSection from './components/configurator/ImagesSection';
import ContactsSection from './components/configurator/ContactsSection';
import RegmailCustomizationSection from './components/configurator/RegmailCustomizationSection';
import GenerateSection, {
  generateEmailHtml,
} from './components/configurator/GenerateSection';
import VisualEditorSection from './components/configurator/VisualEditorSection';
import AccessibilitySection from './components/configurator/AccessibilitySection';
import TemplateToolbar from './components/configurator/TemplateToolbar';
import TextTemplatesSection from './components/configurator/TextTemplatesSection';
import CongressAppLinksSection from './components/configurator/CongressAppLinksSection';

const PREVIEW_STYLE_ID = 'kit-preview-style';
const FONT_SIZE_FIELDS = [
  'bodyFontSize',
  'salutationFontSize',
  'teamNameFontSize',
  'footerFontSize',
  'walletIntroFontSize',
  'walletInstructionsFontSize',
  'calendarButtonFontSize',
  'topPaddingFontSize',
];
const CRITICAL_TEMPLATE_KEYS = new Set([
  'ctaButtons',
  'ctaStyleSlots',
  'calendarProviders',
  'sectionTitles',
  'includeCalendarButton',
  'calendarButtonText',
  'calendarButtonBackgroundColor',
  'calendarButtonTextColor',
  'calendarButtonFontSize',
  'calendarButtonPaddingX',
  'calendarButtonPaddingY',
  'calendarButtonBorderRadius',
  'calendarButtonFileName',
  'calendarEventTitle',
  'calendarEventLocation',
  'calendarEventDescription',
  'calendarEventStart',
  'calendarEventEnd',
  'calendarEventUrl',
  'calendarEventUid',
  'footerText',
  'includeConfidentialityNote',
  'confidentialityNoteFontSize',
  'confidentialityNoteStyles',
  'confidentialityNote',
  'confidentialityNoteTranslations',
  'footerTextColor',
  'footerBackgroundColor',
  'topPaddingText',
  'topPaddingBackgroundColor',
  'topPaddingTextColor',
]);
const LEGACY_FIRST_NAME_PLACEHOLDER = String.raw`\${p.firstName}`;
const LEGACY_LAST_NAME_PLACEHOLDER = String.raw`\${p.lastName}`;
const LEGACY_SALUTATION_DEFAULT = `Dear ${LEGACY_FIRST_NAME_PLACEHOLDER} ${LEGACY_LAST_NAME_PLACEHOLDER},`;
const LEGACY_CLOSING_DEFAULT = 'Sincerely,';
const LEGACY_SALUTATION_TRANSLATIONS = {
  en: LEGACY_SALUTATION_DEFAULT,
  de: `Guten Tag ${LEGACY_FIRST_NAME_PLACEHOLDER} ${LEGACY_LAST_NAME_PLACEHOLDER},`,
  fr: `Chère/Cher ${LEGACY_FIRST_NAME_PLACEHOLDER} ${LEGACY_LAST_NAME_PLACEHOLDER},`,
  ar: '&{anrede}،',
};
const LEGACY_CLOSING_TRANSLATIONS = {
  en: 'Sincerely,',
  de: 'Mit freundlichen Grüßen',
  fr: 'Cordialement,',
  ar: 'مع أطيب التحيات،',
};

const isExactLegacyTranslationMap = (value, legacyMap) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const valueKeys = Object.keys(value);
  const legacyKeys = Object.keys(legacyMap);
  if (valueKeys.length !== legacyKeys.length) return false;
  return legacyKeys.every(key => value[key] === legacyMap[key]);
};

const areTranslationMapsEqual = (left, right) => {
  if (left === right) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') {
    return false;
  }
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every(key => left[key] === right[key]);
};
const buildPreviewChromeStyle = width => `
  :root {
    --kit-preview-width: ${width}px;
  }
  body.kit-preview-body {
    margin: 0;
    min-height: 100vh;
    background: #f4f6fb;
  }
  .kit-preview-shell {
    min-height: 100vh;
    padding: 32px 16px 48px;
    display: flex;
    justify-content: center;
    background: linear-gradient(180deg, rgba(241,245,249,1) 0%, rgba(248,250,252,1) 100%);
  }
  .kit-preview-canvas {
    width: 100%;
    max-width: var(--kit-preview-width);
    background: #ffffff;
    border-radius: 28px;
    box-shadow: 0 35px 70px rgba(15, 23, 42, 0.22);
    overflow: hidden;
  }
  .kit-preview-canvas img {
    display: block;
    max-width: 100%;
    height: auto;
  }
`;

const appendClassName = (attrs, className) => {
  const attrText = attrs || '';
  const classRegex = /class\s*=\s*(['"])([^'"]*)\1/i;
  if (classRegex.test(attrText)) {
    return attrText.replace(classRegex, (match, quote, value) => {
      const classes = value.split(/\s+/);
      if (classes.includes(className)) {
        return ` class=${quote}${value}${quote}`;
      }
      return ` class=${quote}${value} ${className}${quote}`;
    });
  }
  const needsSpace = attrText && !/\s$/.test(attrText);
  return `${attrText}${needsSpace ? ' ' : ''}class="${className}"`;
};

const formatPreviewHtmlForExport = (html, preset) => {
  if (!html) return '';
  const width = Math.min(Math.max(preset?.width || 640, 360), 1000);
  const styleTag = `<style id="${PREVIEW_STYLE_ID}">${buildPreviewChromeStyle(
    width
  )}</style>`;

  let output = html.replace(
    new RegExp(
      `<style[^>]*id=["']${PREVIEW_STYLE_ID}["'][^>]*>[\\s\\S]*?<\\/style>`,
      'i'
    ),
    ''
  );

  if (!/<html[\s>]/i.test(output)) {
    output = `<!DOCTYPE html><html lang="en"><head></head><body>${output}</body></html>`;
  }

  if (/<head[\s>]/i.test(output)) {
    output = output.replace(/<\/head>/i, `${styleTag}</head>`);
  } else {
    output = output.replace(
      /<html([^>]*)>/i,
      `<html$1><head>${styleTag}</head>`
    );
  }

  if (!/kit-preview-shell/i.test(output)) {
    const bodyRegex = /<body([^>]*)>([\s\S]*?)<\/body>/i;
    if (bodyRegex.test(output)) {
      output = output.replace(
        bodyRegex,
        (match, attrs = '', content = '') => {
          const updatedAttrs = appendClassName(attrs, 'kit-preview-body');
          return `<body${updatedAttrs}><div class="kit-preview-shell"><div class="kit-preview-canvas">${content}</div></div></body>`;
        }
      );
    } else {
      output = output.replace(
        /<\/head>/i,
        `</head><body class="kit-preview-body"><div class="kit-preview-shell"><div class="kit-preview-canvas">`
      );
      output = `${output}</div></div></body>`;
    }
  }

  return output;
};

const PlaceholderEditorBar = ({ onSave, onExit }) => (
  <div className="fixed top-0 left-0 right-0 bg-yellow-400 text-black p-3 text-center z-50 shadow-lg flex justify-between items-center px-8">
    <p className="font-semibold">
      <span className="font-bold">Default Settings Editor Mode:</span> Any
      changes you make will become the new defaults for all users.
    </p>
    <div>
      <button
        onClick={onSave}
        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mr-4"
      >
        Save Placeholders
      </button>
      <button
        onClick={onExit}
        className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
      >
        Exit Editor Mode
      </button>
    </div>
  </div>
);

const MenuIcon = () => (
  <svg
    className="w-5 h-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h10" />
  </svg>
);

export function Configurator() {
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('templateSelect');
  const [notification, setNotification] = useState('');
  const {
    settings,
    setSettings,
    resetState,
    undo,
    redo,
    canUndo,
    canRedo,
    initialState,
  } = useConfiguratorState();

  const urlParams = new URLSearchParams(window.location.search);
  const isEditorMode = urlParams.get('mode') === 'placeholder_editor';

  const [isAdminPanelVisible, setAdminPanelVisible] = useState(false);
  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [isPreviewVisible, setPreviewVisible] = useState(false);
  const [isPreviewPinned, setPreviewPinned] = useState(false);

  const [previewHtml, setPreviewHtml] = useState('');
  const [globalSettings, setGlobalSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [templateLibraryRefreshKey, setTemplateLibraryRefreshKey] = useState(0);
  const persistVisualEditorRef = useRef(null);
  const textTemplateSyncRef = useRef({ inFlight: false });
  const previewPanelRef = useRef(null);
  const previewTriggerRef = useRef(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [isSidebarLocked, setIsSidebarLocked] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { width: viewportWidth } = useViewportSize();
  const isTabletOrBelow = viewportWidth <= 1024;

  const SIDEBAR_COLLAPSED_WIDTH = '5rem';
  const SIDEBAR_EXPANDED_WIDTH = '20rem';
  const SIDEBAR_TRANSITION_MS = 420;
  const SIDEBAR_EASING = 'cubic-bezier(0.19, 1, 0.22, 1)';

  const requestTemplateLibraryRefresh = useCallback(() => {
    setTemplateLibraryRefreshKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!Array.isArray(settings?.ctaButtons)) return;
    if (!settings.ctaButtons.some(button => button && (!button.id || typeof button.id !== 'string'))) {
      return;
    }
    setSettings(prev => {
      if (!Array.isArray(prev?.ctaButtons)) return prev;
      let mutated = false;
      const nextButtons = prev.ctaButtons.map(button => {
        if (button && (!button.id || typeof button.id !== 'string')) {
          mutated = true;
          return { ...button, id: uuidv4() };
        }
        return button;
      });
      return mutated ? { ...prev, ctaButtons: nextButtons } : prev;
    });
  }, [settings?.ctaButtons, setSettings]);

  const callPersistVisualEditor = useCallback(
    (silent = true) => {
      const handler = persistVisualEditorRef.current;
      if (typeof handler === 'function') {
        try {
          return handler(silent);
        } catch (error) {
          console.warn('Persist visual editor handler failed', error);
        }
      }
      return null;
    },
    []
  );

  const registerPersistHandler = useCallback(handler => {
    persistVisualEditorRef.current = handler;
  }, []);

  const handlePreviewDeviceChange = useCallback(value => {
    setPreviewDevice(ensureDeviceKey(value));
  }, []);

  useEffect(() => {
    if (isSidebarLocked) {
      setIsSidebarCollapsed(false);
    }
  }, [isSidebarLocked]);

  useEffect(() => {
    if (!isTabletOrBelow) {
      setMobileSidebarOpen(false);
    }
  }, [isTabletOrBelow]);

  useEffect(() => {
    if (activeSection === 'calendarButton' && !settings?.includeCalendarButton) {
      setActiveSection('content');
    }
    if (activeSection === 'congressAppLinks' && !settings?.showCongressAppLinks) {
      setActiveSection('content');
    }
  }, [activeSection, settings?.includeCalendarButton, settings?.showCongressAppLinks]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!isTabletOrBelow) {
      document.body.style.overflow = '';
      return undefined;
    }
    const original = document.body.style.overflow;
    document.body.style.overflow = isMobileSidebarOpen ? 'hidden' : original || '';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isMobileSidebarOpen, isTabletOrBelow]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!isPreviewPinned && !isPreviewVisible) return undefined;
    const handlePointerDown = event => {
      const target = event.target;
      if (
        previewPanelRef.current?.contains(target) ||
        previewTriggerRef.current?.contains(target)
      ) {
        return;
      }
      setPreviewPinned(false);
      setPreviewVisible(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isPreviewPinned, isPreviewVisible]);

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        if (user?.role && user.role.name === 'Admin') {
          e.preventDefault();
          setAdminPanelVisible(v => !v);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user]);

  const handleNavigate = sectionKey => {
    setActiveSection(sectionKey);
    setPreviewPinned(false);
    setPreviewVisible(false);
    window.scrollTo(0, 0);
    if (isTabletOrBelow) {
      setMobileSidebarOpen(false);
    }
  };

  const showNotification = useCallback(
    (message, type = 'success') => {
      setNotification({ message, type });
      setTimeout(() => setNotification(''), 3000);
    },
    [setNotification]
  );

  const syncFontSizesFromGlobal = useCallback((draft, nextSize) => {
    if (!Number.isFinite(nextSize)) return draft;
    const next = { ...draft };

    FONT_SIZE_FIELDS.forEach(key => {
      next[key] = nextSize;
    });

    if (Array.isArray(next.sectionTitles)) {
      next.sectionTitles = next.sectionTitles.map(item =>
        item ? { ...item, fontSize: nextSize } : item
      );
    }

    if (Array.isArray(next.ctaButtons)) {
      next.ctaButtons = next.ctaButtons.map(button =>
        button ? { ...button, fontSize: nextSize } : button
      );
    }

    return next;
  }, []);

  const normalizeLoadedTemplateSettings = useCallback(
    incomingSettings => {
      if (!incomingSettings || typeof incomingSettings !== 'object') {
        return incomingSettings;
      }

      const defaults = globalSettings
        ? deriveDefaultsFromGlobals(globalSettings)
        : null;
      let changed = false;
      const next = { ...incomingSettings };

      const linkMode = next?.textTemplateLink?.mode || 'auto';
      if (next.visualEditor && next.textTemplateLink?.id && linkMode !== 'manual') {
        delete next.visualEditor;
        changed = true;
      }

      if (defaults) {
        if (
          next.salutationInput === LEGACY_SALUTATION_DEFAULT &&
          defaults.salutationInput &&
          defaults.salutationInput !== LEGACY_SALUTATION_DEFAULT
        ) {
          next.salutationInput = defaults.salutationInput;
          changed = true;
        }
        if (
          next.closingText === LEGACY_CLOSING_DEFAULT &&
          defaults.closingText &&
          defaults.closingText !== LEGACY_CLOSING_DEFAULT
        ) {
          next.closingText = defaults.closingText;
          changed = true;
        }
        if (
          isExactLegacyTranslationMap(
            next.salutationTranslations,
            LEGACY_SALUTATION_TRANSLATIONS
          ) &&
          defaults.salutationTranslations &&
          !isExactLegacyTranslationMap(
            defaults.salutationTranslations,
            LEGACY_SALUTATION_TRANSLATIONS
          ) &&
          !areTranslationMapsEqual(
            next.salutationTranslations,
            defaults.salutationTranslations
          )
        ) {
          next.salutationTranslations = defaults.salutationTranslations;
          changed = true;
        }
        if (
          isExactLegacyTranslationMap(
            next.closingTextTranslations,
            LEGACY_CLOSING_TRANSLATIONS
          ) &&
          defaults.closingTextTranslations &&
          !isExactLegacyTranslationMap(
            defaults.closingTextTranslations,
            LEGACY_CLOSING_TRANSLATIONS
          ) &&
          !areTranslationMapsEqual(
            next.closingTextTranslations,
            defaults.closingTextTranslations
          )
        ) {
          next.closingTextTranslations = defaults.closingTextTranslations;
          changed = true;
        }
      }

      return changed ? next : incomingSettings;
    },
    [globalSettings]
  );

  useEffect(() => {
    if (!settings) return;
    const normalized = normalizeLoadedTemplateSettings(settings);
    if (normalized !== settings) {
      setSettings(normalized);
    }
  }, [settings, normalizeLoadedTemplateSettings, setSettings]);

  const updateSetting = useCallback(
    (key, value) => {
      if (key === null) {
        setSettings(normalizeLoadedTemplateSettings(value));
        return;
      }

      setSettings(prev => {
        let next = { ...prev, [key]: value };

        if (key === 'bodyFontSize') {
          next = syncFontSizesFromGlobal(next, value);
        }

        if (
          key !== 'visualEditor' &&
          prev?.visualEditor &&
          activeSection !== 'visualEditor' &&
          CRITICAL_TEMPLATE_KEYS.has(key)
        ) {
          const { visualEditor, ...rest } = next;
          return rest;
        }

        return next;
      });
    },
    [setSettings, activeSection, syncFontSizesFromGlobal, normalizeLoadedTemplateSettings]
  );

  const applyTextTemplateToCurrentDesign = useCallback(
    template => {
      if (!template) return;
      setSettings(prev => {
        const { settings: nextSettings, changedKeys } = applyTextTemplateToSettings(
          prev,
          template,
          { mode: prev?.textTemplateLink?.mode || 'auto' }
        );
        if (
          prev?.visualEditor &&
          changedKeys.some(key => CRITICAL_TEMPLATE_KEYS.has(key))
        ) {
          const { visualEditor, ...rest } = nextSettings;
          return rest;
        }
        return nextSettings;
      });
      showNotification?.('Text template linked to this design.', 'success');
    },
    [setSettings, showNotification]
  );

  const syncTextTemplateNow = useCallback(() => {
    const link = settings?.textTemplateLink;
    if (!link?.id) {
      showNotification?.('No linked text template to sync.', 'error');
      return Promise.resolve();
    }
    if (textTemplateSyncRef.current.inFlight) return Promise.resolve();
    textTemplateSyncRef.current.inFlight = true;
    return api
      .getTextTemplate(link.id)
      .then(template => {
        setSettings(prev => {
          const { settings: nextSettings, changedKeys } = applyTextTemplateToSettings(
            prev,
            template,
            { mode: link.mode || 'auto' }
          );
          if (
            prev?.visualEditor &&
            changedKeys.some(key => CRITICAL_TEMPLATE_KEYS.has(key))
          ) {
            const { visualEditor, ...rest } = nextSettings;
            return rest;
          }
          return nextSettings;
        });
        showNotification?.('Text template synced.', 'success');
      })
      .catch(err => {
        showNotification?.(err.message || 'Failed to sync text template', 'error');
      })
      .finally(() => {
        textTemplateSyncRef.current.inFlight = false;
      });
  }, [setSettings, settings?.textTemplateLink, showNotification]);

  useEffect(() => {
    const link = settings?.textTemplateLink;
    if (!link || !link.id || link.mode === 'manual') return;
    if (textTemplateSyncRef.current.inFlight) return;

    const lastSynced = link.templateUpdatedAt
      ? new Date(link.templateUpdatedAt).getTime()
      : null;

    textTemplateSyncRef.current.inFlight = true;
    api
      .getTextTemplate(link.id)
      .then(template => {
        const updatedAt = template?.updatedAt || template?.updated_at;
        const updatedTime = updatedAt ? new Date(updatedAt).getTime() : null;
        if (!updatedTime || (lastSynced && updatedTime <= lastSynced)) {
          return;
        }
        setSettings(prev => {
          const { settings: nextSettings, changedKeys } = applyTextTemplateToSettings(
            prev,
            template,
            { mode: link.mode || 'auto' }
          );
          if (
            prev?.visualEditor &&
            changedKeys.some(key => CRITICAL_TEMPLATE_KEYS.has(key))
          ) {
            const { visualEditor, ...rest } = nextSettings;
            return rest;
          }
          return nextSettings;
        });
      })
      .catch(() => {})
      .finally(() => {
        textTemplateSyncRef.current.inFlight = false;
      });
  }, [
    setSettings,
    settings?.textTemplateLink,
    settings?.textTemplateLink?.id,
    settings?.textTemplateLink?.templateUpdatedAt,
    settings?.textTemplateLink?.mode,
  ]);

  const handleSavePlaceholders = async () => {
    const currentGlobalSettings = await api.getGlobalSettings();
    const newPlaceholders = {
      ...currentGlobalSettings.placeholders,
      salutation: settings.salutationInput,
      eventAbbreviation: settings.eventAbbreviation,
      teamName: settings.organizingTeamName,
      banners: settings.banners,
      includeTopPadding: settings.includeTopPadding,
      topPaddingHeight: settings.topPaddingHeight,
      topPaddingBackgroundColor: settings.topPaddingBackgroundColor,
      topPaddingTextColor: settings.topPaddingTextColor,
      topPaddingText: settings.topPaddingText,
      socialTextInput: settings.socialTextInput,
      contactEmail: settings.contactEmail,
      contactPhone: settings.contactPhone,
      websiteUrl: settings.websiteUrl,
      websiteDisplayText: settings.websiteDisplayText,
      copyrightYear: settings.copyrightYear,
      bodyBackgroundColor: settings.bodyBackgroundColor,
      bodyTextColor: settings.bodyTextColor,
      linkColor: settings.linkColor,
      dividerColor: settings.dividerColor,
      footerBackgroundColor: settings.footerBackgroundColor,
      footerTextColor: settings.footerTextColor,
      templateFontFamily: settings.templateFontFamily,
      bodyFontSize: settings.bodyFontSize,
      paragraphLineHeight: settings.paragraphLineHeight,
      tableBorderRadius: settings.tableBorderRadius,
      salutationFontSize: settings.salutationFontSize,
      salutationStyles: settings.salutationStyles,
      closingText: settings.closingText,
      salutationTranslations: settings.salutationTranslations,
      closingTextTranslations: settings.closingTextTranslations,
      teamNameFontSize: settings.teamNameFontSize,
      teamNameStyles: settings.teamNameStyles,
      footerFontSize: settings.footerFontSize,
      footerStyles: settings.footerStyles,
      footerPadding: settings.footerPadding,
      footerBorderRadius: settings.footerBorderRadius,
      footerTextTranslations: settings.footerTextTranslations,
      lineWidth: settings.lineWidth,
      socialMediaBackgroundColor: settings.socialMediaBackgroundColor,
      showCongressAppLinks: settings.showCongressAppLinks,
      congressAppHeadline: settings.congressAppHeadline,
      congressAppBackgroundColor: settings.congressAppBackgroundColor,
      congressAppLinks: settings.congressAppLinks,
      walletBackgroundColor: settings.walletBackgroundColor,
      walletIntroText: settings.walletIntroText,
      walletIntroFontSize: settings.walletIntroFontSize,
      walletIntroStyles: settings.walletIntroStyles,
      walletInstructionsText: settings.walletInstructionsText,
      walletInstructionsFontSize: settings.walletInstructionsFontSize,
      walletInstructionsStyles: settings.walletInstructionsStyles,
      calendarProviders: settings.calendarProviders,
      calendarButtonText: settings.calendarButtonText,
      calendarButtonBackgroundColor: settings.calendarButtonBackgroundColor,
      calendarButtonTextColor: settings.calendarButtonTextColor,
      calendarButtonFontSize: settings.calendarButtonFontSize,
      calendarButtonPaddingX: settings.calendarButtonPaddingX,
      calendarButtonPaddingY: settings.calendarButtonPaddingY,
      calendarButtonBorderRadius: settings.calendarButtonBorderRadius,
    };

    await api.updateGlobalSettings({
      ...currentGlobalSettings,
      placeholders: newPlaceholders,
    });

    // Refresh local global defaults so Reset/Clear use the latest values
    try {
      const fresh = await api.getGlobalSettings();
      setGlobalSettings(fresh);
    } catch (e) {
      // If fetch fails, at least update placeholders locally
      setGlobalSettings(prev => ({
        ...(prev || {}),
        placeholders: newPlaceholders,
      }));
    }

    const templates = await api.getTemplates(user);
    const standardTemplate = templates.global.find(
      t => t.id === 'standard-template-id'
    );
    if (standardTemplate) {
      const updatedTemplate = {
        ...standardTemplate,
        salutationInput: settings.salutationInput,
        eventAbbreviation: settings.eventAbbreviation,
        organizingTeamName: settings.organizingTeamName,
        banners: settings.banners,
        includeTopPadding: settings.includeTopPadding,
        topPaddingHeight: settings.topPaddingHeight,
        topPaddingBackgroundColor: settings.topPaddingBackgroundColor,
        topPaddingTextColor: settings.topPaddingTextColor,
        topPaddingText: settings.topPaddingText,
        socialTextInput: settings.socialTextInput,
        contactEmail: settings.contactEmail,
        contactPhone: settings.contactPhone,
        websiteUrl: settings.websiteUrl,
        websiteDisplayText: settings.websiteDisplayText,
      };
      await api.updateGlobalTemplate(updatedTemplate);
    }

    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(k => {
        if (k.startsWith('currentConfig')) sessionStorage.removeItem(k);
      });
    } catch {}

    showNotification('Default placeholders have been updated!', 'success');
  };

  const handleExitEditorMode = () => {
    window.location.search = '';
  };

  useEffect(() => {
    if (settings?.templateFontFamily) {
      loadGoogleFont(settings.templateFontFamily);
    }
  }, [settings?.templateFontFamily]);

  useEffect(() => {
    async function fetchGlobal() {
      try {
        const data = await api.getGlobalSettings();
        setGlobalSettings(data);
      } catch (error) {
        console.warn('Failed to load global settings; using local fallback.', error);
        setGlobalSettings({
          placeholders: {},
          defaults: {},
          imageUrls: {},
        });
      } finally {
        setIsLoading(false);
      }
    }
    fetchGlobal();
  }, []);

  useEffect(() => {
    if (!settings || !globalSettings) return;
    const html = generateEmailHtml(settings, globalSettings);
    setPreviewHtml(html);
  }, [settings, globalSettings]);

  const normalizedPreviewDevice = ensureDeviceKey(previewDevice);
  const previewDevicePreset =
    DEVICE_PRESETS[normalizedPreviewDevice] || DEVICE_PRESETS.desktop;
  const safeViewportWidth = Math.max(viewportWidth || 0, 320);
  const previewPopoverAvailableWidth = Math.max(safeViewportWidth - 32, 280);
  const previewPanelWidth = isTabletOrBelow
    ? Math.min(previewPopoverAvailableWidth, previewDevicePreset.containerWidth)
    : previewDevicePreset.containerWidth;
  const previewPanelMinWidth = isTabletOrBelow
    ? Math.min(previewPopoverAvailableWidth, 360)
    : 360;
  const previewPanelMaxWidth = isTabletOrBelow
    ? `${previewPopoverAvailableWidth}px`
    : 'calc(100vw - 160px)';
  const previewHtmlWithChrome = useMemo(
    () => formatPreviewHtmlForExport(previewHtml, previewDevicePreset),
    [previewHtml, previewDevicePreset]
  );

  const handlePreviewMouseEnter = () => {
    if (!isPreviewPinned) setPreviewVisible(true);
  };
  const handlePreviewMouseLeave = () => {
    if (!isPreviewPinned) setPreviewVisible(false);
  };
  const handlePreviewClick = () => {
    setPreviewPinned(prev => {
      const nextPinned = !prev;
      setPreviewVisible(nextPinned);
      return nextPinned;
    });
  };
  const handleCopyHtml = useCallback(async () => {
    const html = String(previewHtml || '').trim();
    if (!html) {
      showNotification('No HTML available to copy yet.', 'error');
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(html);
      } else {
        const helper = document.createElement('textarea');
        helper.value = html;
        helper.setAttribute('readonly', '');
        helper.style.position = 'fixed';
        helper.style.opacity = '0';
        document.body.appendChild(helper);
        helper.select();
        document.execCommand('copy');
        document.body.removeChild(helper);
      }
      showNotification('HTML copied to clipboard.', 'success');
    } catch (error) {
      showNotification('Failed to copy HTML.', 'error');
    }
  }, [previewHtml, showNotification]);

  if (isLoading || !settings) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <p>Loading configurator...</p>
      </div>
    );
  }

  // Map each section to the list of settings keys it controls
  const sectionKeysMap = {
    appearance: [
      'bodyBackgroundColor',
      'bodyTextColor',
      'linkColor',
      'dividerColor',
      'templateFontFamily',
      'bodyFontSize',
      'paragraphLineHeight',
      'tableBorderRadius',
      'lineWidth',
    ],
    content: [
      'includeWalletButtons',
      'salutationInput',
      'salutationFontSize',
      'salutationStyles',
      'sectionTitles',
      'ctaButtons',
      'showCongressAppLinks',
      'congressAppHeadline',
      'congressAppBackgroundColor',
      'congressAppLinks',
    ],
    calendarButton: [
      'calendarProviders',
      'includeCalendarButton',
      'calendarButtonText',
      'calendarButtonBackgroundColor',
      'calendarButtonTextColor',
      'calendarButtonFontSize',
      'calendarButtonPaddingX',
      'calendarButtonPaddingY',
      'calendarButtonBorderRadius',
      'calendarButtonFileName',
      'calendarEventTitle',
      'calendarEventLocation',
      'calendarEventDescription',
      'calendarEventStart',
      'calendarEventEnd',
      'calendarEventUrl',
      'calendarEventUid',
    ],
    wallet: [
      'walletBackgroundColor',
      'walletTextColor',
      'walletTextAlign',
      'walletIntroText',
      'walletIntroFontSize',
      'walletIntroStyles',
      'walletInstructionsText',
      'walletInstructionsFontSize',
      'walletInstructionsStyles',
    ],
    regmailCustomization: [
      'eventName',
      'hostingOrganisation',
      'eventLocation',
      'eventStartDate',
      'eventEndDate',
      'eventTime',
      'paymentDueDate',
      'eventEmail',
    ],
    images: [
      'includeOuterBorder',
      'outerBorderColor',
      'includeTopPadding',
      'topPaddingHeight',
      'topPaddingFontSize',
      'topPaddingBackgroundColor',
      'topPaddingTextColor',
      'topPaddingText',
      'includeFooter',
      'footerPadding',
      'footerFontSize',
      'footerBackgroundColor',
      'footerTextColor',
      'footerText',
      'footerStyles',
      'banners',
    ],
    contacts: [
      'includeCoName',
      'includeAssociation',
      'includeAddress',
      'organizingTeamName',
      'teamNameFontSize',
      'teamNameStyles',
      'showEmail',
      'showPhone',
      'showWebsite',
      'contactOrder',
      'contactEmail',
      'contactPhone',
      'websiteUrl',
      'websiteDisplayText',
      'showSocialMedia',
      'socialTextInput',
      'socialMediaBackgroundColor',
      'socialIconSize',
      'socialIconBorderRadius',
      'socialLinks',
    ],
    accessibility: [
      'bodyBackgroundColor',
      'bodyTextColor',
      'linkColor',
      'footerBackgroundColor',
      'footerTextColor',
      'ctaButtons',
      'sectionTitles',
    ],
  };

  const handleResetCurrentSection = () => {
    const keys = sectionKeysMap[activeSection];
    if (!keys) return;
    const confirmMsg =
      'Reset this section to default values? This cannot be undone.';
    if (!window.confirm(confirmMsg)) return;
    const defaults = globalSettings
      ? deriveDefaultsFromGlobals(globalSettings)
      : initialState || {};
    const next = { ...settings };
    const cloneVal = v => {
      if (Array.isArray(v))
        return v.map(item =>
          typeof item === 'object' ? JSON.parse(JSON.stringify(item)) : item
        );
      if (v && typeof v === 'object') return JSON.parse(JSON.stringify(v));
      return v;
    };
    keys.forEach(k => {
      if (Object.prototype.hasOwnProperty.call(defaults, k)) {
        next[k] = cloneVal(defaults[k]);
      } else {
        delete next[k];
      }
    });
    setSettings(next);
    showNotification('Section reset to defaults.', 'success');
  };

  const parseMaybeJson = value => {
    if (!value || typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const parsedData = parseMaybeJson(settings?.data);

  const resolveExplicitRegmailFlag = source =>
    source &&
    typeof source === 'object' &&
    Object.prototype.hasOwnProperty.call(source, 'regmailEnabled')
      ? Boolean(source.regmailEnabled)
      : undefined;

  const regmailHeuristics = (() => {
    const categoryValue = settings?.category || parsedData?.category;
    const templateName = settings?.name || parsedData?.name;

    const regmailBlock =
      settings?.regmail || parsedData?.regmail || parsedData;
    let hasRegmailFields = false;
    if (regmailBlock && typeof regmailBlock === 'object') {
      const regmailKeys = [
        'eventName',
        'hostingOrganisation',
        'eventLocation',
        'eventStartDate',
        'eventEndDate',
        'eventTime',
        'paymentDueDate',
        'eventEmail',
      ];
      hasRegmailFields = regmailKeys.some(key =>
        Object.prototype.hasOwnProperty.call(regmailBlock, key)
      );
    }

    if (
      categoryValue &&
      String(categoryValue).toLowerCase().includes('regmail')
    )
      return true;
    if (templateName && String(templateName).toLowerCase().includes('regmail'))
      return true;
    if (hasRegmailFields) return true;

    const keys = [
      'eventName',
      'hostingOrganisation',
      'eventLocation',
      'eventStartDate',
      'eventEndDate',
      'eventTime',
      'paymentDueDate',
      'eventEmail',
    ];
    const hasKey = (obj, key) =>
      obj ? Object.prototype.hasOwnProperty.call(obj, key) : false;
    return keys.some(key => hasKey(settings, key) || hasKey(parsedData, key));
  })();

  const explicitRegmailSetting = resolveExplicitRegmailFlag(settings);
  const explicitRegmailFromData = resolveExplicitRegmailFlag(parsedData);

  const regmailEnabled =
    explicitRegmailSetting ?? explicitRegmailFromData ?? regmailHeuristics;
  const sections = [
    {
      key: 'templateSelect',
      title: 'Template Selection',
      description: 'Choose a template to start with.',
      Component: TemplateSelectSection,
      props: {
        refreshSignal: templateLibraryRefreshKey,
        onEventsLoaded: setAvailableEvents,
      },
    },
    {
      key: 'visualEditor',
      title: null,
      description: null,
      Component: VisualEditorSection,
      props: {
        globalSettings,
        onRegisterPersist: registerPersistHandler,
        previewDevice: normalizedPreviewDevice,
        onPreviewDeviceChange: handlePreviewDeviceChange,
      },
    },
    {
      key: 'appearance',
      title: 'Appearance',
      description: 'Customize the visual style of your email template.',
      Component: AppearanceSection,
      props: {
        customFonts: globalSettings?.customFonts || [],
      },
    },
    {
      key: 'content',
      title: 'Content & Text',
      description:
        'Configure the salutation, signature, and dynamic content sections.',
      Component: ContentSection,
      props: {
        regmailEnabled,
        languages: globalSettings?.aiConfig?.languages || [],
      },
    },
    {
      key: 'congressAppLinks',
      title: 'Congress App Links',
      description: 'Add app store badges and links for your event.',
      Component: CongressAppLinksSection,
      hidden: !settings?.showCongressAppLinks,
      props: {
        regmailEnabled,
        globalSettings,
      },
    },
    {
      key: 'calendarButton',
      title: 'Calendar Button',
      description: 'Let recipients add the event to their calendar with a single click.',
      Component: CalendarButtonSection,
      props: {
        regmailEnabled,
        globalSettings,
      },
      hidden: !settings?.includeCalendarButton,
    },
    {
      key: 'wallet',
      title: 'Wallet Customization',
      description: 'Settings for Apple & Google Wallet buttons.',
      Component: WalletSection,
      props: {
        globalSettings,
      },
      hidden: !settings?.includeWalletButtons,
    },
    {
      key: 'walletImageResizer',
      title: 'Wallet Image Resizer',
      description: 'Resize and pad Apple Wallet and Google Wallet artwork.',
      Component: WalletImageResizerSection,
      props: {
        regmailEnabled,
      },
    },
    {
      key: 'regmailCustomization',
      title: 'Regmail Customization',
      description: 'Customize event-specific variables for Regmail template.',
      Component: RegmailCustomizationSection,
      hidden: !regmailEnabled,
    },
    {
      key: 'images',
      title: 'Layout & Media',
      description:
        'Configure layout options, header/footer areas, and banner images.',
      Component: ImagesSection,
      props: {
        regmailEnabled,
      },
    },
    {
      key: 'contacts',
      title: 'Contact Information',
      description: 'Configure contact details and social media links.',
      Component: ContactsSection,
    },
    {
      key: 'textTemplates',
      title: 'Text Templates',
      description: 'Create, reorder, and batch-generate text templates with event variables.',
      Component: TextTemplatesSection,
      props: {
        events: availableEvents,
        globalSettings,
        activeTemplateEventId: settings?.event_id || settings?.eventId || null,
        onApplyTextTemplate: applyTextTemplateToCurrentDesign,
        activeTextTemplateLink: settings?.textTemplateLink || null,
        onSyncTextTemplate: syncTextTemplateNow,
      },
    },
    {
      key: 'accessibility',
      title: 'Accessibility Checker',
      description: 'Identify and resolve colour contrast and heading issues.',
      Component: AccessibilitySection,
      props: {
        globalSettings,
      },
    },
    {
      key: 'generate',
      title: 'Generate HTML',
      description: 'Review your settings and generate the final HTML.',
      Component: GenerateSection,
      props: {
        onNavigate: handleNavigate,
        showNotification,
        onUpdate: updateSetting,
      },
    },
  ];

  return (
    <div
      className={`min-h-screen font-sans text-slate-900 ${
        isAdminPanelVisible ? 'overflow-hidden h-screen' : ''
      } ${isEditorMode ? 'pt-20' : ''}`}
    >
      {isEditorMode && (
        <PlaceholderEditorBar
          onSave={handleSavePlaceholders}
          onExit={handleExitEditorMode}
        />
      )}

      {notification && (
        <div
          className={`fixed bottom-5 right-5 ${notification.type === 'error' ? 'bg-red-600' : 'bg-green-600'} text-white px-5 py-3 rounded-lg shadow-lg z-[60] animate-fadeIn`}
        >
          {notification.message}
        </div>
      )}

      {isAdminPanelVisible && user.role && user.role.name === 'Admin' && (
        <AdminPanel
          onClose={() => setAdminPanelVisible(false)}
          currentSettings={settings}
          showNotification={showNotification}
          onGlobalSettingsSaved={async () => {
            try {
              const fresh = await api.getGlobalSettings();
              setGlobalSettings(fresh);
              showNotification(
                'Site configuration updated. Preview refreshed.',
                'success'
              );
            } catch (e) {
              showNotification(
                'Updated settings saved, but failed to refresh preview.',
                'error'
              );
            }
          }}
        />
      )}
      {isProfileModalVisible && (
        <ProfileModal onClose={() => setIsProfileModalVisible(false)} />
      )}

      <div className="flex min-h-screen">
        <Sidebar
          activeSection={activeSection}
          onNavigate={handleNavigate}
          showWallet={Boolean(settings?.includeWalletButtons)}
          showCalendar={Boolean(settings?.includeCalendarButton)}
          showCongressAppLinks={Boolean(settings?.showCongressAppLinks)}
          logout={logout}
          clearConfig={() => {
            const defaults = globalSettings
              ? deriveDefaultsFromGlobals(globalSettings)
              : initialState || {};
            resetState(defaults);
            showNotification('All settings reset to defaults.', 'success');
          }}
      onProfileClick={() => setIsProfileModalVisible(true)}
      settings={settings}
      collapsed={isSidebarCollapsed}
      onCollapsedChange={setIsSidebarCollapsed}
      locked={isSidebarLocked}
      onLockedChange={setIsSidebarLocked}
      isMobile={isTabletOrBelow}
      mobileOpen={isTabletOrBelow ? isMobileSidebarOpen : true}
      onRequestCloseMobile={() => setMobileSidebarOpen(false)}
    />
    {isTabletOrBelow && (
      <div
        className={`fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          isMobileSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileSidebarOpen(false)}
      />
    )}
    <main
      className="flex-1 px-4 py-6 sm:px-6 lg:px-8"
      style={
        isTabletOrBelow
          ? undefined
          : {
              marginLeft: isSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
              transition: `margin-left ${SIDEBAR_TRANSITION_MS}ms ${SIDEBAR_EASING}`,
            }
      }
    >
          <div className="space-y-8">
            <div className="sticky top-4 z-20">
              <div className="rounded-2xl border border-white/60 bg-white/90 p-3 shadow-lg shadow-primary/10 backdrop-blur supports-[backdrop-filter]:backdrop-blur-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center flex-1">
                    {isTabletOrBelow && (
                      <button
                        type="button"
                        onClick={() => setMobileSidebarOpen(true)}
                        className="history-btn w-full sm:w-auto justify-center lg:hidden"
                        aria-controls="kit-config-sidebar"
                        aria-expanded={isMobileSidebarOpen}
                      >
                        <MenuIcon />
                        Sections
                      </button>
                    )}
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                      <button type="button" onClick={undo} disabled={!canUndo} className="history-btn w-full sm:w-auto">
                        <UndoIcon /> Undo
                      </button>
                      <button type="button" onClick={redo} disabled={!canRedo} className="history-btn w-full sm:w-auto">
                        <RedoIcon /> Redo
                      </button>
                      <button
                        type="button"
                        onClick={handleResetCurrentSection}
                        disabled={!sectionKeysMap[activeSection]}
                        className="history-btn w-full sm:w-auto"
                        title="Reset current section to defaults"
                      >
                        <ClearIcon /> Reset Section
                      </button>
                    </div>
                  </div>
                  <div
                    className="relative flex-1 lg:flex-none"
                    onMouseEnter={handlePreviewMouseEnter}
                    onMouseLeave={handlePreviewMouseLeave}
                    ref={previewTriggerRef}
                  >
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={handleCopyHtml}
                        className="history-btn w-full sm:w-auto"
                      >
                        Copy HTML
                      </button>
                      <button
                        type="button"
                        onClick={handlePreviewClick}
                        className={`history-btn w-full sm:w-auto ${isPreviewPinned ? 'bg-blue-100 text-primary' : ''}`}
                        aria-pressed={isPreviewPinned}
                      >
                        <EyeIcon className="w-5 h-5 mr-1" /> Preview
                      </button>
                    </div>
                    {(isPreviewVisible || isPreviewPinned) && (
                      <div
                        ref={previewPanelRef}
                        className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 p-4 flex flex-col gap-3"
                        style={{
                          minWidth: `${previewPanelMinWidth}px`,
                          width: `${previewPanelWidth}px`,
                          maxWidth: previewPanelMaxWidth,
                          maxHeight: '80vh',
                        }}
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <ResponsiveDeviceToggle
                            value={normalizedPreviewDevice}
                            onChange={handlePreviewDeviceChange}
                            variant="compact"
                          />
                        </div>
                        <div className="flex-1 overflow-auto rounded-2xl border border-gray-200 bg-gray-50/80 p-3">
                          <div
                            className="mx-auto bg-white rounded-xl overflow-hidden border border-gray-200 shadow-lg"
                            style={{
                              maxWidth: `${previewDevicePreset.width}px`,
                              width: '100%',
                            }}
                          >
                            <iframe
                              srcDoc={previewHtmlWithChrome || ''}
                              title="Quick Preview"
                              className="w-full border-0"
                              style={{ minHeight: `${previewDevicePreset.height}px` }}
                            />
                          </div>
                        </div>
                        {isPreviewPinned && (
                          <button
                            type="button"
                            className="absolute -top-4 -right-4 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-lg transition hover:border-primary hover:text-primary"
                            onClick={() => {
                              setPreviewPinned(false);
                              setPreviewVisible(false);
                            }}
                            aria-label="Close preview"
                          >
                            <span className="text-xl font-semibold leading-none" aria-hidden="true">
                              ×
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <TemplateToolbar
                settings={settings}
                onUpdate={updateSetting}
                user={user}
                showNotification={showNotification}
                onNavigate={handleNavigate}
                persistVisualEditor={callPersistVisualEditor}
                onTemplatesSaved={requestTemplateLibraryRefresh}
                events={availableEvents}
              />
            </div>
            <div className="space-y-8">
              {sections
                .filter(s => !s.hidden)
                .map(({ key, title, description, Component, props }) => (
                  <Section
                    key={key}
                    title={title}
                    description={description}
                    isActive={activeSection === key}
                  >
                    <Component
                      settings={settings}
                      onUpdate={updateSetting}
                      onNavigate={handleNavigate}
                      showNotification={showNotification}
                      {...props}
                    />
                  </Section>
                ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
